/**
 * 救援 L1：ODM GLB 贴到 Mapbox
 * - Mapbox 2.x = WebGL1，three≥0.163 = WebGL2 → 禁止共享 gl
 * - 用独立 WebGL2 画布 + map.transform.customLayerMatrix() 同步投影
 */
import mapboxgl from 'mapbox-gl';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { prepareModelForMapAttach } from './orthoGlbPrepare';
import { isTerrain3dWanted, restoreMapTerrain3d } from './dashboard/mapTerrainHelpers';
import { attachPoseGizmo, detachPoseGizmo, paintPoseGizmo } from './orthoGlbPoseGizmo';
import {
  addLoadAssembleFx,
  inferModelHeight,
  peekAssembleSize,
  rememberAssembleSize,
  removeLoadAssembleFx
} from './orthoGlbLoadFx';

const LAYER_PREFIX = 'zhxt-ortho-glb-';
const STATE_KEY = '__zhxtOrthoGlbState';

/** ODM 导出的 GLB 常带 Draco；解码器放在 public/draco/gltf */
let sharedDracoLoader = null;
function getDracoLoader() {
  if (!sharedDracoLoader) {
    sharedDracoLoader = new DRACOLoader();
    sharedDracoLoader.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/gltf/`);
    sharedDracoLoader.setDecoderConfig({ type: 'js' }); // 内网优先 JS 解码，免 WASM CORS
  }
  return sharedDracoLoader;
}

function createGltfLoader() {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDracoLoader());
  return loader;
}

function layerIdOf(id) {
  return `${LAYER_PREFIX}${id}`;
}

function normalizeBounds(b) {
  if (!b) return null;
  if (Array.isArray(b) && b.length >= 4) {
    const [west, south, east, north] = b.map(Number);
    if ([west, south, east, north].every(Number.isFinite)) {
      return { west, south, east, north };
    }
  }
  const west = Number(b.west);
  const south = Number(b.south);
  const east = Number(b.east);
  const north = Number(b.north);
  if ([west, south, east, north].every(Number.isFinite)) {
    return { west, south, east, north };
  }
  return null;
}

function boundsSpanMeters(bb) {
  const fp = boundsSizeMeters(bb);
  if (!fp) return 80;
  return Math.max(fp.width, fp.depth);
}

function boundsSizeMeters(bb) {
  if (!bb) return null;
  const latMid = (bb.south + bb.north) / 2;
  const width = Math.abs(bb.east - bb.west) * 111320 * Math.max(Math.cos((latMid * Math.PI) / 180), 0.15);
  const depth = Math.abs(bb.north - bb.south) * 111320;
  if (![width, depth].every((n) => Number.isFinite(n) && n > 0)) return null;
  return {
    width: Math.min(400, Math.max(5, width)),
    depth: Math.min(400, Math.max(5, depth))
  };
}

/** 建模刚上图时的默认位姿：范围中心、海拔 0、不旋转、缩放 1 */
export function resolveInitialModelPose(layer) {
  const bb = normalizeBounds(layer?.bounds);
  const pose = layer?.modelPose || {};
  const lng = bb
    ? (bb.west + bb.east) / 2
    : Number(pose.lng);
  const lat = bb
    ? (bb.south + bb.north) / 2
    : Number(pose.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return {
    lng,
    lat,
    altitude: 0,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    scale: 1
  };
}

/** 从 bounds / modelPose 解析锚点与姿态（旋转单位：度） */
export function resolveModelPose(layer) {
  const pose = layer?.modelPose || {};
  const bb = normalizeBounds(layer?.bounds);
  const lng = Number(pose.lng);
  const lat = Number(pose.lat);
  const fromPose = Number.isFinite(lng) && Number.isFinite(lat);
  const centerLng = fromPose ? lng : (bb ? (bb.west + bb.east) / 2 : null);
  const centerLat = fromPose ? lat : (bb ? (bb.south + bb.north) / 2 : null);
  if (!Number.isFinite(centerLng) || !Number.isFinite(centerLat)) return null;
  return {
    lng: centerLng,
    lat: centerLat,
    altitude: Number.isFinite(Number(pose.altitude)) ? Number(pose.altitude) : 0,
    // Mapbox 自定义层已有 Y 轴取负；ODM 正向模型用 0。若仍房顶朝下，可在 modelPose 显式设 rotateX=180
    rotateX: Number.isFinite(Number(pose.rotateX)) ? Number(pose.rotateX) : 0,
    rotateY: Number.isFinite(Number(pose.rotateY)) ? Number(pose.rotateY) : 0,
    rotateZ: Number.isFinite(Number(pose.rotateZ)) ? Number(pose.rotateZ) : 0,
    scale: Number.isFinite(Number(pose.scale)) && Number(pose.scale) > 0 ? Number(pose.scale) : 1
  };
}

function getStateMap(map) {
  if (!map[STATE_KEY]) map[STATE_KEY] = {};
  return map[STATE_KEY];
}

export function isOrthoGlbOnMap(map, layerId) {
  if (!map) return false;
  return !!getStateMap(map)[layerIdOf(layerId)];
}

/**
 * 已贴地时开关锚点拖拽（位姿微调：拖橙钉改 lng/lat）
 */
export function setOrthoGlbMarkerDraggable(map, layerId, draggable, onDrag) {
  if (!map || layerId == null) return false;
  const handle = getStateMap(map)[layerIdOf(layerId)];
  if (!handle?.marker) return false;
  try {
    if (handle._poseDragHandler) {
      handle.marker.off('drag', handle._poseDragHandler);
      handle.marker.off('dragend', handle._poseDragHandler);
      handle._poseDragHandler = null;
    }
    handle.marker.setDraggable(!!draggable);
    if (draggable && typeof onDrag === 'function') {
      handle._poseDragHandler = () => {
        try {
          const ll = handle.marker.getLngLat();
          if (!ll) return;
          handle.pose.lng = ll.lng;
          handle.pose.lat = ll.lat;
          onDrag({ lng: ll.lng, lat: ll.lat });
          map.triggerRepaint();
        } catch (_) { /* ignore */ }
      };
      handle.marker.on('drag', handle._poseDragHandler);
      handle.marker.on('dragend', handle._poseDragHandler);
    }
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * 微调时在模型上显示三轴箭头（东/北/上）；关闭时卸掉
 */
export function setOrthoGlbGizmoActive(map, layerId, active, onMove) {
  if (!map || layerId == null) return false;
  const handle = getStateMap(map)[layerIdOf(layerId)];
  if (!handle) return false;
  if (active) return attachPoseGizmo(handle, onMove);
  detachPoseGizmo(handle);
  try {
    map.triggerRepaint();
  } catch (_) { /* ignore */ }
  return true;
}

/**
 * 已贴地时实时微调位姿（不重下 GLB）
 * @param {object} partial 可含 lng/lat/altitude/rotateX/rotateY/rotateZ/scale
 * @returns {boolean} 是否找到叠层并已应用
 */
export function updateOrthoGlbPose(map, layerId, partial = {}) {
  if (!map || layerId == null) return false;
  const handle = getStateMap(map)[layerIdOf(layerId)];
  if (!handle?.pose) return false;
  const p = handle.pose;
  // 必须改原对象：paint 闭包读的是同一份 pose，换新对象会拖不动
  if (Number.isFinite(Number(partial.lng))) p.lng = Number(partial.lng);
  if (Number.isFinite(Number(partial.lat))) p.lat = Number(partial.lat);
  if (Number.isFinite(Number(partial.altitude))) {
    p.altitude = Number(partial.altitude);
    handle.followTerrain = p.altitude === 0;
  }
  if (partial.followTerrain === true) handle.followTerrain = true;
  if (partial.followTerrain === false) handle.followTerrain = false;
  if (Number.isFinite(Number(partial.rotateX))) p.rotateX = Number(partial.rotateX);
  if (Number.isFinite(Number(partial.rotateY))) p.rotateY = Number(partial.rotateY);
  if (Number.isFinite(Number(partial.rotateZ))) p.rotateZ = Number(partial.rotateZ);
  if (Number.isFinite(Number(partial.scale)) && Number(partial.scale) > 0) {
    p.scale = Number(partial.scale);
  }
  try {
    handle.marker?.setLngLat?.([p.lng, p.lat]);
  } catch (_) { /* ignore */ }
  // 开算时 scale 已烘焙进网格尺寸；相对 mountScale 调 root
  try {
    const mountScale = handle.mountScale > 0 ? handle.mountScale : 1;
    handle.root?.scale?.setScalar?.(p.scale / mountScale);
  } catch (_) { /* ignore */ }
  try {
    map.triggerRepaint();
  } catch (_) { /* ignore */ }
  return true;
}

function queryTerrainSnap(map, lng, lat) {
  try {
    const hasTerrain = !!map.getTerrain?.();
    const elev = map.queryTerrainElevation?.([lng, lat], { exaggerated: hasTerrain });
    if (Number.isFinite(elev)) return elev;
  } catch (_) { /* ignore */ }
  return null;
}

function resolveAttachPose(map, layer, opts = {}) {
  const pose = resolveModelPose(layer);
  if (!pose) throw new Error('图层缺少 bounds/modelPose，无法定位 3D');
  if (Number.isFinite(Number(opts.rotateX))) pose.rotateX = Number(opts.rotateX);
  const lng = Number.isFinite(opts.displayLng) ? opts.displayLng : pose.lng;
  const lat = Number.isFinite(opts.displayLat) ? opts.displayLat : pose.lat;
  const bb = normalizeBounds(layer?.bounds);
  const CLEARANCE_M = Number.isFinite(Number(opts.clearanceM)) ? Number(opts.clearanceM) : 1.5;
  const followTerrain = !Number.isFinite(pose.altitude) || pose.altitude === 0;
  let altitude = pose.altitude;
  if (followTerrain) {
    const elev = queryTerrainSnap(map, lng, lat);
    if (Number.isFinite(elev)) altitude = elev;
  }
  if (!Number.isFinite(altitude)) altitude = 0;
  return {
    pose,
    lng,
    lat,
    bb,
    followTerrain,
    baseAltitude: altitude + (followTerrain ? CLEARANCE_M : 0),
    targetMeters: boundsSpanMeters(bb) * pose.scale
  };
}

/**
 * 勾选即上图：先出占位粗体，再可选粗 GLB、最后细 GLB。
 * 返回时占位已可见；`pending` 在细模就绪或失败时结束。
 */
export async function attachOrthoGlbOnMap(map, layer, opts = {}) {
  if (!map || !layer?.id) throw new Error('地图或图层无效');
  const id = layerIdOf(layer.id);
  const stateMap = getStateMap(map);
  if (stateMap[id]) return { active: true, pending: Promise.resolve(stateMap[id]) };
  if (!opts.glbUrl) throw new Error('缺少 GLB 地址');

  const { pose, lng, lat, bb, followTerrain, baseAltitude, targetMeters } = resolveAttachPose(map, layer, opts);
  try {
    if (isTerrain3dWanted()) restoreMapTerrain3d(map, { skipPitch: true });
  } catch (_) { /* ignore */ }
  const MIN_PITCH = 35;
  try {
    if (typeof map.getMaxPitch === 'function' && map.getMaxPitch() < 70) {
      map.setMaxPitch(85);
    }
  } catch (_) { /* ignore */ }

  const handle = createOverlayShell(map, {
    id,
    lng,
    lat,
    baseAltitude,
    rotateX: pose.rotateX,
    rotateY: pose.rotateY,
    rotateZ: pose.rotateZ,
    scale: pose.scale,
    minPitch: MIN_PITCH,
    followTerrain
  });
  handle.abort = opts.signal ? null : new AbortController();
  handle.layerId = layer.id;
  handle.layerBounds = bb;
  const signal = opts.signal || handle.abort.signal;
  const fp = boundsSizeMeters(bb);
  const cached = peekAssembleSize(layer.id);
  const scale = pose.scale > 0 ? pose.scale : 1;
  addLoadAssembleFx(handle, {
    width: cached?.width ?? (fp ? fp.width * scale : undefined),
    depth: cached?.depth ?? (fp ? fp.depth * scale : undefined),
    height: cached?.height,
    targetMeters
  });
  stateMap[id] = handle;

  const pending = (async () => {
    try {
      if (opts.coarseGlbUrl) {
        try {
          const coarse = await fetchGlbArrayBuffer(opts.coarseGlbUrl, signal);
          if (signal?.aborted || handle.disposed) return handle;
          await loadModelIntoRoot(handle, coarse, { targetMeters });
          opts.onCoarseReady?.();
        } catch (err) {
          if (signal?.aborted || handle.disposed) return handle;
          console.warn('[ortho-glb] 粗模跳过', err?.message || err);
        }
      }
      const fine = await fetchGlbArrayBuffer(opts.glbUrl, signal);
      if (signal?.aborted || handle.disposed) return handle;
      await loadModelIntoRoot(handle, fine, { targetMeters });
      opts.onReady?.({ ok: true, modelSize: handle.modelSize });
      return handle;
    } catch (err) {
      if (signal?.aborted || handle.disposed) return handle;
      opts.onError?.(err);
      throw err;
    }
  })();
  handle.ready = pending;
  return { active: true, lng, lat, pending };
}

/**
 * 切换地图附着 3D。打开时立刻占位，细模后台补齐。
 */
export async function toggleOrthoGlbOnMap(map, layer, opts = {}) {
  if (!map || !layer?.id) throw new Error('地图或图层无效');
  const id = layerIdOf(layer.id);
  const stateMap = getStateMap(map);
  if (stateMap[id]) {
    stateMap[id].abort?.abort?.();
    removeGlbOverlay(map, id);
    return { active: false };
  }
  return attachOrthoGlbOnMap(map, layer, opts);
}

async function fetchGlbArrayBuffer(url, signal) {
  const res = await fetch(url, { credentials: 'same-origin', signal });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j.error || j.message || '';
    } catch (_) {
      detail = await res.text().catch(() => '');
    }
    throw new Error(`GLB 下载失败 HTTP ${res.status}${detail ? `：${detail}` : ''}`);
  }
  const buf = await res.arrayBuffer();
  if (!buf || buf.byteLength < 100) throw new Error('GLB 文件过小或为空');
  return buf;
}

function disposeObject3d(obj) {
  obj?.traverse?.((node) => {
    if (node.geometry) node.geometry.dispose?.();
    if (node.material) {
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((m) => {
        m.map?.dispose?.();
        m.dispose?.();
      });
    }
  });
}

function clearRootChildren(root) {
  if (!root) return;
  const kids = [...(root.children || [])];
  kids.forEach((child) => {
    root.remove(child);
    disposeObject3d(child);
  });
}


function parseGlbBuffer(glbBuffer) {
  return new Promise((resolve, reject) => {
    const loader = createGltfLoader();
    loader.parse(
      glbBuffer,
      '',
      (gltf) => {
        const model = gltf.scene || gltf.scenes?.[0];
        if (!model) {
          reject(new Error('GLB 内无场景'));
          return;
        }
        resolve(model);
      },
      (err) => reject(new Error(`GLB 解析失败：${err?.message || err}`))
    );
  });
}

async function loadModelIntoRoot(handle, glbBuffer, { targetMeters }) {
  if (!handle || handle.disposed) return;
  const model = await parseGlbBuffer(glbBuffer);
  if (handle.disposed) {
    disposeObject3d(model);
    return;
  }
  model.traverse((obj) => {
    if (obj.isMesh && obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((mat) => {
        mat.side = THREE.DoubleSide;
        mat.depthWrite = true;
        mat.depthTest = true;
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = -2;
        mat.polygonOffsetUnits = -2;
        mat.needsUpdate = true;
      });
    }
  });
  const prep = prepareModelForMapAttach(model, { targetMeters });
  console.info('[ortho-glb] 贴图预处理', prep);
  const fp = boundsSizeMeters(handle.layerBounds);
  const scale = handle.pose?.scale > 0 ? handle.pose.scale : 1;
  rememberAssembleSize(handle.layerId, {
    width: fp ? fp.width * scale : Math.max(prep.sizeX, prep.sizeY),
    depth: fp ? fp.depth * scale : Math.min(prep.sizeX, prep.sizeY),
    height: inferModelHeight(prep.sizeX, prep.sizeY, prep.sizeZ)
  });
  removeLoadAssembleFx(handle);
  clearRootChildren(handle.root);
  handle.root.add(model);
  handle.modelSize = prep;
  try {
    handle.map.triggerRepaint();
  } catch (_) { /* ignore */ }
}

function removeGlbOverlay(map, id) {
  const stateMap = getStateMap(map);
  const handle = stateMap[id];
  if (!handle) return;
  handle.disposed = true;
  try {
    removeLoadAssembleFx(handle);
  } catch (_) { /* ignore */ }
  try {
    detachPoseGizmo(handle);
  } catch (_) { /* ignore */ }
  try {
    handle.abort?.abort?.();
  } catch (_) { /* ignore */ }
  try {
    map.off('render', handle.onRender);
    map.off('resize', handle.onResize);
    if (handle.onPitchGuard) map.off('pitch', handle.onPitchGuard);
    if (handle.onPitchGuard) map.off('pitchend', handle.onPitchGuard);
  } catch (_) { /* ignore */ }
  try {
    // 关掉 GLB 后仍按立体开关保持/恢复山体（与 3D 共存，不再靠关山体贴模）
    restoreMapTerrain3d(map, { skipPitch: true });
  } catch (_) {
    /* ignore */
  }
  try {
    handle.marker?.remove?.();
  } catch (_) { /* ignore */ }
  try {
    handle.root?.traverse?.((obj) => {
      if (obj.geometry) obj.geometry.dispose?.();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          m.map?.dispose?.();
          m.dispose?.();
        });
      }
    });
  } catch (_) { /* ignore */ }
  try {
    handle.renderer?.dispose?.();
  } catch (_) { /* ignore */ }
  try {
    handle.overlayCanvas?.remove?.();
  } catch (_) { /* ignore */ }
  delete stateMap[id];
  try {
    map.triggerRepaint();
  } catch (_) { /* ignore */ }
}

/**
 * 清除所有贴地图 3D 叠层（含残留黑画布）。
 * 框选/密度预览前调用，避免盖住底图看起来「一片空白」。
 */
export function removeAllOrthoGlbOverlays(map) {
  if (!map) return;
  const stateMap = getStateMap(map);
  Object.keys(stateMap).forEach((id) => removeGlbOverlay(map, id));
  try {
    const host = map.getCanvasContainer?.() || map.getContainer?.();
    host?.querySelectorAll?.('.zhxt-ortho-glb-overlay')?.forEach((el) => el.remove());
  } catch (_) { /* ignore */ }
}

/**
 * 画板工作时暂时隐藏贴地 3D 画布（不卸载），避免挡住/干扰地图点击。
 * @param {boolean} suppressed
 */
export function setOrthoGlbOverlaysSuppressed(map, suppressed) {
  if (!map) return;
  const hide = !!suppressed;
  try {
    const host = map.getCanvasContainer?.() || map.getContainer?.();
    host?.querySelectorAll?.('.zhxt-ortho-glb-overlay')?.forEach((el) => {
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.visibility = hide ? 'hidden' : 'visible';
    });
  } catch (_) { /* ignore */ }
  if (!hide) {
    try {
      map.triggerRepaint();
    } catch (_) { /* ignore */ }
  }
}

function createOverlayShell(map, {
  id,
  lng,
  lat,
  baseAltitude = 0,
  rotateX = 0,
  rotateY = 0,
  rotateZ = 0,
  scale = 1,
  minPitch = 35,
  followTerrain = false
}) {
    // 可变位姿：微调 UI 会改 pose，paint 每帧读取
    const pose = {
      lng,
      lat,
      altitude: baseAltitude,
      rotateX,
      rotateY,
      rotateZ,
      scale: Number.isFinite(Number(scale)) && Number(scale) > 0 ? Number(scale) : 1
    };
    const mountScale = pose.scale;
    let handle = null;

    /** 微调会改 handle.pose；paint 必须读最新一份，不能死盯创建时的闭包 */
    const livePose = () => (handle && handle.pose) ? handle.pose : pose;

    /** 跟山体贴地时用夸张高程；很高 zoom 再略抬，避免沉入网格 */
    const altitudeForZoom = () => {
      let z = 16;
      try {
        z = map.getZoom?.() ?? 16;
      } catch (_) { /* ignore */ }
      const extra = Math.max(0, z - 18) * 1.5;
      const p = livePose();
      if (handle?.followTerrain && map.getTerrain?.()) {
        const elev = queryTerrainSnap(map, p.lng, p.lat);
        if (Number.isFinite(elev)) return elev + 1.5 + extra;
      }
      return p.altitude + extra;
    };

    const mercatorAt = (alt) => {
      const p = livePose();
      return mapboxgl.MercatorCoordinate.fromLngLat([p.lng, p.lat], alt);
    };

    const camera = new THREE.Camera();
    camera.matrixAutoUpdate = false;
    camera.projectionMatrixAutoUpdate = false;

    const scene = new THREE.Scene();
    const light1 = new THREE.DirectionalLight(0xffffff, 1.2);
    light1.position.set(50, 100, 80);
    scene.add(light1);
    const light2 = new THREE.DirectionalLight(0xffffff, 0.65);
    light2.position.set(-40, 60, 40);
    scene.add(light2);
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));

    const root = new THREE.Group();
    scene.add(root);

    // 叠在整个地图容器上，确保盖住底图 canvas
    const overlayCanvas = document.createElement('canvas');
    overlayCanvas.className = 'zhxt-ortho-glb-overlay';
    overlayCanvas.dataset.layerId = id;
    overlayCanvas.style.cssText = [
      'position:absolute',
      'inset:0',
      'width:100%',
      'height:100%',
      'pointer-events:none',
      // 高于 Mapbox 画布与常见标注层，避免被正射栅格「视觉盖住」
      'z-index:8'
    ].join(';');
    const host = map.getCanvasContainer() || map.getContainer();
    host.appendChild(overlayCanvas);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas: overlayCanvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
        // 放大时 mercator 尺度跨度大，对数深度减轻模型被「切掉/沉底」
        logarithmicDepthBuffer: true
      });
    } catch (e) {
      overlayCanvas.remove();
      throw new Error(`WebGL2 初始化失败：${e.message || e}`);
    }
    renderer.setClearColor(0x000000, 0);
    renderer.autoClear = true;
    // Three 初始化后可能改写 canvas 样式；强制不拦截指针，否则画板点不中地图
    overlayCanvas.style.setProperty('pointer-events', 'none', 'important');

    const syncSize = () => {
      const mapCanvas = map.getCanvas();
      const w = mapCanvas.clientWidth || map.getContainer().clientWidth;
      const h = mapCanvas.clientHeight || map.getContainer().clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      renderer.setPixelRatio(dpr);
      renderer.setSize(w, h, false);
    };
    syncSize();

    // 锚点标记：方便确认飞到了正确位置
    let marker = null;
    try {
      marker = new mapboxgl.Marker({ color: '#e85d04', scale: 0.9 })
        .setLngLat([pose.lng, pose.lat])
        .setPopup(new mapboxgl.Popup({ offset: 16 }).setText('3D 锚点'))
        .addTo(map);
    } catch (_) { /* ignore */ }

    const paint = () => {
      if (!renderer || !map.transform?.customLayerMatrix) return;
      syncSize();

      // 每帧按 zoom 更新锚点高度，并用相对平移降低 float32 精度丢失
      const alt = altitudeForZoom();
      const mc = mercatorAt(alt);
      const meterScale = mc.meterInMercatorCoordinateUnits();
      const pNow = livePose();
      const rotX = (pNow.rotateX * Math.PI) / 180;
      const rotY = (pNow.rotateY * Math.PI) / 180;
      const rotZ = (pNow.rotateZ * Math.PI) / 180;

      const matrix = map.transform.customLayerMatrix();
      const rotationX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), rotX);
      const rotationY = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), rotY);
      const rotationZ = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), rotZ);

      // 以地图中心为参考做相对平移，减轻高 zoom 下模型抖动/沉入底图
      let ox = 0;
      let oy = 0;
      let oz = 0;
      try {
        const center = map.getCenter?.();
        if (center) {
          const cc = mapboxgl.MercatorCoordinate.fromLngLat([center.lng, center.lat], 0);
          ox = cc.x;
          oy = cc.y;
          oz = cc.z;
        }
      } catch (_) { /* ignore */ }

      const m = new THREE.Matrix4().fromArray(matrix);
      // 先平移到地图中心，再画相对偏移的模型（等价于绝对 mercator，数值更稳）
      const l = new THREE.Matrix4()
        .makeTranslation(mc.x - ox, mc.y - oy, mc.z - oz)
        .scale(new THREE.Vector3(meterScale, -meterScale, meterScale))
        .multiply(rotationX)
        .multiply(rotationY)
        .multiply(rotationZ);
      const origin = new THREE.Matrix4().makeTranslation(ox, oy, oz);

      camera.projectionMatrix.copy(m.multiply(origin).multiply(l));
      camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
      renderer.resetState();
      renderer.clear(true, true, true);
      renderer.render(scene, camera);
      if (handle) {
        paintPoseGizmo(handle, {
          map, camera, renderer, mc, ox, oy, oz, meterScale, matrix
        });
      }
    };

    const onRender = () => {
      paint();
    };
    const onResize = () => {
      syncSize();
      paint();
    };

    map.on('render', onRender);
    map.on('resize', onResize);

    const onPitchGuard = () => {
      try {
        const p = map.getPitch?.() || 0;
        if (p < minPitch) {
          map.setPitch(minPitch);
        }
      } catch (_) { /* ignore */ }
    };
    map.on('pitch', onPitchGuard);
    map.on('pitchend', onPitchGuard);

    handle = {
      id,
      map,
      renderer,
      scene,
      camera,
      root,
      overlayCanvas,
      marker,
      onRender,
      onResize,
      onPitchGuard,
      modelSize: null,
      pose,
      mountScale,
      followTerrain: !!followTerrain,
      disposed: false
    };

    paint();
    try {
      map.triggerRepaint();
    } catch (_) { /* ignore */ }
    return handle;
}
